/**
 * ACHETER UN POINT DE REPÈRE — logique pure (ROADMAP 2.3).
 *
 * ══ Pourquoi ce n'est pas une branche dans `tryPurchase` ════════════════════
 *
 * `tryPurchase` répond à une question en pièces : « ce bâtiment est-il débloqué, et
 * peux-tu le payer ? ». Un repère ne se débloque pas, ne coûte pas de pièces, et ne
 * s'achète qu'une fois. Trois conditions différentes, trois refus différents : les
 * empiler dans la même fonction aurait donné une fonction dont la moitié des champs ne
 * veut rien dire selon l'entrée — et c'est ainsi qu'un `coinsAfter` finit par être lu
 * pour un achat en gemmes.
 *
 * ══ Un seul exemplaire, et le prestige les remet en vente ═══════════════════
 *
 * L'unicité se lit sur ce que le joueur POSSÈDE, pas sur un drapeau à part. Deux
 * conséquences, toutes deux voulues :
 *
 * · le prestige remet la ville à zéro, donc les repères reviennent achetables — chaque
 *   ère rouvre 460 gemmes d'objectif, ce que le §1 réclame ;
 * · **revendre un repère le rend achetable à nouveau**. C'est assumé : les gemmes
 *   dépensées ne sont PAS rendues (voir `sellRefundsNothing` dans les contrôles), donc
 *   racheter coûte plein tarif. Un joueur qui déplace sa ville ne perd rien d'autre que
 *   le geste, et personne ne peut fabriquer de gemmes avec.
 */

import { LANDMARKS, landmarkByType } from '@/config/landmarks';
import type { BuildingCounts } from './economy';

export type LandmarkRefusal =
  /** Ce type n'est pas un repère. */
  | 'unknown-type'
  /** Déjà posé — un seul exemplaire par ère. */
  | 'already-owned'
  /** Pas assez de gemmes. */
  | 'too-few-gems';

export interface LandmarkPurchase {
  readonly ok: boolean;
  readonly refusal?: LandmarkRefusal;
  /** Prix en gemmes. Rendu même en cas de refus : l'écran l'affiche pour expliquer. */
  readonly cost: number;
  readonly gemsAfter: number;
}

/**
 * Tente l'achat. Ne modifie rien : rend le résultat, à l'appelant d'appliquer.
 *
 * Le motif du refus est explicite plutôt que booléen — c'est lui qui décide de ce que
 * voit le joueur : « déjà posé » désactive la carte, « pas assez de gemmes » ouvre la
 * boutique (G§1, défaut n° 3 : l'offre ne doit jamais être muette au moment où le joueur
 * veut payer).
 */
export function tryBuyLandmark(
  type: string,
  counts: BuildingCounts,
  gems: number,
): LandmarkPurchase {
  const definition = landmarkByType(type);
  if (definition === undefined) {
    return { ok: false, refusal: 'unknown-type', cost: 0, gemsAfter: gems };
  }

  const cost = definition.gemCost;

  if ((counts[type] ?? 0) > 0) {
    return { ok: false, refusal: 'already-owned', cost, gemsAfter: gems };
  }

  if (gems < cost) {
    return { ok: false, refusal: 'too-few-gems', cost, gemsAfter: gems };
  }

  return { ok: true, cost, gemsAfter: gems - cost };
}

/**
 * Ce qu'il reste à acheter, en gemmes — la capacité de l'ère en cours.
 *
 * **Dérivée, jamais recopiée** (règle 15) : c'est ce nombre que `gemEconomy.test.ts`
 * additionne pour mesurer le chiffre du ROADMAP 2.7. Une constante écrite à côté se
 * désynchroniserait au premier prix retouché, et la mesure mentirait sans rougir.
 */
export function landmarksRemainingGems(counts: BuildingCounts): number {
  let total = 0;
  for (const landmark of LANDMARKS) {
    if ((counts[landmark.type] ?? 0) === 0) total += landmark.gemCost;
  }
  return total;
}
